import { Button } from "@/components/ui/button";
import type { MaterialCondition, ReportMaterialsForm } from "@/Wist-blockchain/collections/reportMaterials/types";
import { uid } from "../utils/uid-generator";

const IVA_OPTIONS = [0, 10, 12, 15]
const CONDITIONS: MaterialCondition[] = ["Nuevo", "Usado"];

interface MaterialsTableProps {
    materials: MaterialRow[];
    onAdd: () => void;
    onRemove: (id: string) => void;
    onUpdate: (id: string, key: keyof ReportMaterialsForm, value: string | number) => void;
    total: number;
}

export type MaterialRow = ReportMaterialsForm & { id: string };

export const createMaterial = (order: number): MaterialRow => ({
    id: uid(), order, idReport: "", name: "", code: "", quantity: 1, condition: "Nuevo", unitCost: 0, ivaPercent: 15
});

export function MaterialsTable({ materials, onAdd, onRemove, onUpdate, total }: MaterialsTableProps) {
    const totalIva = materials.reduce((sum, m) => sum + (m.quantity * m.unitCost * (m.ivaPercent / 100)), 0)
    const totalWithIva = total + totalIva

    const clearRow = (id: string) => {
        onUpdate(id, "name", "")
        onUpdate(id, "code", "")
        onUpdate(id, "quantity", 1)
        onUpdate(id, "condition", "Nuevo")
        onUpdate(id, "unitCost", 0)
        onUpdate(id, "ivaPercent", 0)
    }
    const cell = "w-full bg-transparent border border-transparent hover:border-border/60 focus:border-primary focus:bg-muted/30 rounded px-2 py-1 text-foreground placeholder:text-muted-foreground/30 outline-none transition-colors text-xs";
    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {materials.length} material{materials.length !== 1 ? "es" : ""}
                </span>
                <Button type="button" size="sm" variant="outline" onClick={onAdd}
                    className="cursor-pointer text-primary border-primary/30 hover:bg-primary/5 text-xs h-7 px-3">
                    + Agregar
                </Button>
            </div>
            <div className="rounded-lg border border-border/50 overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="border-b border-border/40 bg-muted/30">
                            {["#", "Nombre", "Código", "Cant.", "Condición", "Costo unit.", "IVA %", "Subtotal", ""].map((h, i) => (
                                <th key={i} className="px-3 py-2 text-left text-muted-foreground font-medium text-[11px] uppercase tracking-wide whitespace-nowrap">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border/30">
                        {materials.map((m) => {
                            const subtotal = m.quantity * m.unitCost
                            const ivaAmt = subtotal * (m.ivaPercent / 100)
                            return (
                                <tr key={m.id} className="hover:bg-muted/20 transition-colors">
                                    <td className="px-3 py-1.5 text-muted-foreground/40 font-mono text-center text-[11px] w-8">{m.order}</td>
                                    <td className="px-1.5 py-1.5 min-w-30"><input type="text" placeholder="Nombre del repuesto" value={m.name} onChange={(e) => onUpdate(m.id, "name", e.target.value)} className={cell} /></td>
                                    <td className="px-1.5 py-1.5 w-24"><input type="text" placeholder="REF-001" value={m.code} onChange={(e) => onUpdate(m.id, "code", e.target.value)} className={cell} /></td>
                                    <td className="px-1.5 py-1.5 w-16"><input type="number" min={1} value={m.quantity} onChange={(e) => onUpdate(m.id, "quantity", Number(e.target.value))} className={cell} /></td>
                                    <td className="px-1.5 py-1.5 w-24">
                                        <select value={m.condition} onChange={(e) => onUpdate(m.id, "condition", e.target.value)} className={`${cell} cursor-pointer`}>
                                            {CONDITIONS.map((c) => <option key={c}>{c}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-1.5 py-1.5 w-24"><input type="number" min={0} step={0.01} value={m.unitCost} onChange={(e) => onUpdate(m.id, "unitCost", Number(e.target.value))} className={cell} /></td>
                                    <td className="px-1.5 py-1.5 w-20">
                                        <select value={m.ivaPercent} onChange={(e) => onUpdate(m.id, "ivaPercent", Number(e.target.value))} className={`${cell} cursor-pointer`}>
                                            {IVA_OPTIONS.map(p => <option key={p} value={p}>{p}%</option>)}
                                        </select>
                                    </td>
                                    <td className="px-3 py-1.5 w-24">
                                        <div className="text-primary font-mono tabular-nums font-medium">{(subtotal + ivaAmt).toFixed(2)}</div>
                                        {m.ivaPercent > 0 && (
                                            <div className="text-[10px] text-muted-foreground/60 tabular-nums">+{ivaAmt.toFixed(2)} IVA</div>
                                        )}
                                    </td>
                                    <td className="px-2 py-1.5 w-8">
                                        <button type="button"
                                            onClick={() => materials.length === 1 ? clearRow(m.id) : onRemove(m.id)}
                                            className="text-muted-foreground/20 hover:text-destructive transition-colors p-1 rounded cursor-pointer">✕</button>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
            <div className="flex flex-col items-end gap-1.5 pt-2 border-t border-border/40">
                <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">Subtotal</span>
                    <span className="text-sm font-medium text-foreground tabular-nums">${total.toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">IVA</span>
                    <span className="text-sm font-medium text-foreground tabular-nums">${totalIva.toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-3 pt-1 border-t border-border/40">
                    <span className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">Total</span>
                    <span className="text-base font-bold text-primary tabular-nums">${totalWithIva.toFixed(2)}</span>
                </div>
            </div>
        </div>
    );
}
